import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  Tooltip as ChartTooltip, CartesianGrid, ReferenceLine, Cell, LabelList,
} from 'recharts';
import { useTheme } from '../contexts/ThemeContext';
import { minMax } from '../lib/utils';
import { inversionApi } from '../lib/api';
import Crossplot from './Crossplot';
import { crossplotIterations, iterationOptions, fitAtIteration } from '../lib/crossplot';

/** Modal width (px) from which "Convergence & misfit" and "Crossplot" share one view. */
export const WIDE_MODAL_PX = 900;

/** The datafit endpoint's column block as the crossplot's data-fit shape (lib/crossplot.js). */
export function datafitToFit(data) {
  const p = data?.points || {};
  if (!p.measured?.length) return { rhoa_meas: [], rhoa_calc: [] };
  return { rhoa_meas: p.measured, rhoa_calc: p.calculated, seq: p.seq, iteration: data.iteration ?? null };
}
import { X_AXES, Y_LABELS, availableAxes, buildLcurveSeries } from '../lib/lcurve';


const fieldBox = (C) => ({ fontSize: 11, color: C.text3, padding: '18px 10px', textAlign: 'center', background: C.bg3, borderRadius: 8 });

/** Pick stored models of one organization (of `projectId` when given, the map panel's
 *  project) and plot misfit vs norm / damping. `activeId` (a stored model) starts selected. */
function LCurveTab({ C, projectId = null, activeId = null }) {
  const [models, setModels] = useState(null); // null = loading
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(activeId ? [activeId] : []);
  const [points, setPoints] = useState(null);
  const [busy, setBusy] = useState(false);
  const [xKey, setXKey] = useState('norm');

  useEffect(() => {
    let live = true;
    inversionApi.list(projectId ? { project_id: projectId } : undefined)
      .then((res) => { if (live) setModels(res.data || []); })
      .catch(() => { if (live) { setModels([]); setError('Could not load stored inversions.'); } });
    return () => { live = false; };
  }, [projectId]);

  const toggle = (id) => setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const plot = async () => {
    setBusy(true); setError('');
    try {
      const res = await inversionApi.lcurve(selected);
      const pts = res.data?.points || [];
      setPoints(pts);
      setXKey(availableAxes(pts)[0] || 'norm');
    } catch (err) {
      setPoints(null);
      setError(String(err?.response?.data?.detail || err?.message || 'Request failed'));
    } finally { setBusy(false); }
  };

  const axes = points ? availableAxes(points) : [];
  const series = points ? buildLcurveSeries(points, xKey) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="lcurve-tab">
      <div style={{ fontSize: 11, color: C.text2 }}>
        Select two or more stored inversions of the same data run with different damping. The L-curve plots the
        final data misfit against the model norm (EarthImager) or the regularisation value (BERT lambda).
      </div>
      {models === null && <div style={fieldBox(C)}>Loading stored inversions…</div>}
      {models && models.length === 0 && (
        <div style={fieldBox(C)} data-testid="lcurve-no-models">
          No stored inversions {projectId ? 'in this project' : 'in your organizations'}. Import several inversion results (same line, different damping) from the map's Inversion panel first; they are stored with the project.
        </div>
      )}
      {models && models.length > 0 && (
        <div style={{ maxHeight: 150, overflow: 'auto', border: `1px solid ${C.border}`, borderRadius: 8, padding: 6 }}>
          {models.map((m) => (
            <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: C.text1, padding: '2px 4px', cursor: 'pointer' }}>
              <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggle(m.id)} />
              <span style={{ flex: 1 }}>{m.name}</span>
              <span style={{ color: C.text3 }}>{m.rms_percent != null ? `RMS ${m.rms_percent}%` : ''}</span>
            </label>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={plot} disabled={busy || selected.length < 2}
          style={{ fontSize: 11, padding: '5px 12px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, cursor: 'pointer', opacity: busy || selected.length < 2 ? 0.5 : 1 }}>
          {busy ? 'Loading…' : `Plot L-curve (${selected.length} selected)`}
        </button>
        {axes.length > 1 && (
          <label style={{ fontSize: 10, color: C.text2 }}>
            X axis{' '}
            <select value={xKey} onChange={(e) => setXKey(e.target.value)}
              style={{ fontSize: 11, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, borderRadius: 6, padding: '2px 6px' }}>
              {axes.map((k) => <option key={k} value={k}>{X_AXES[k].label}</option>)}
            </select>
          </label>
        )}
      </div>
      {error && <div style={{ fontSize: 11, color: C.red }} data-testid="lcurve-error">{error}</div>}
      {series && series.rows.length < 2 && (
        <div style={fieldBox(C)} data-testid="lcurve-empty">
          Fewer than two of the selected models carry a misfit together with a {X_AXES[xKey].label.toLowerCase()}.
          Models imported from files without a convergence table or regularisation setting cannot be placed.
        </div>
      )}
      {series && series.rows.length >= 2 && (
        <div style={{ height: 260 }} data-testid="lcurve-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series.rows} margin={{ top: 16, right: 24, bottom: 18, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" dataKey="x" domain={['auto', 'auto']} tick={{ fontSize: 9, fill: C.text3 }} stroke={C.border}
                scale={series.rows.every((r) => r.x > 0) ? 'log' : 'auto'}
                label={{ value: X_AXES[series.xKey].label, position: 'insideBottom', offset: -8, fontSize: 10, fill: C.text3 }} />
              <YAxis type="number" dataKey="y" domain={['auto', 'auto']} tick={{ fontSize: 9, fill: C.text3 }} stroke={C.border}
                label={{ value: Y_LABELS[series.yKey], angle: -90, position: 'insideLeft', fontSize: 10, fill: C.text3 }} />
              <ChartTooltip contentStyle={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11 }}
                itemStyle={{ color: C.text1 }} labelStyle={{ color: C.text0 }}
                formatter={(v, n) => [v, n === 'y' ? Y_LABELS[series.yKey] : n]} labelFormatter={(v) => `${X_AXES[series.xKey].label}: ${v}`} />
              <Line dataKey="y" stroke={C.cyan} strokeWidth={2} dot={{ r: 4, fill: C.cyan }} isAnimationActive={false}>
                <LabelList dataKey="label" position="top" style={{ fontSize: 9, fill: C.text2 }} />
              </Line>
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      {series && series.excluded.length > 0 && (
        <ul style={{ fontSize: 10, color: C.amber, margin: 0, paddingLeft: 16 }} data-testid="lcurve-excluded">
          {series.excluded.map((e) => <li key={e.id}>{e.name || e.id}: {e.reason}</li>)}
        </ul>
      )}
    </div>
  );
}

/**
 * QCChartsModal — EarthImager-style inversion QC charts (6.10)
 *
 * - RMS convergence: iteration vs RMS misfit, target reference line
 * - Data misfit histogram: per-cell misfit distribution, outliers in red
 *
 * - Crossplot tab: calculated vs measured apparent resistivity (model.dataFit: rhoa_meas, rhoa_calc),
 *   log axes, 1:1 line, sign-coded (components/Crossplot.js). Only one iteration's data fit is stored.
 * - L-curve tab: final misfit vs model norm (or regularisation) across several stored
 *   inversions of the same data, from GET /api/inversion/lcurve. Electrode contact
 *   resistance lives on the Survey card / raw QC report (it is a property of the raw file).
 *
 * Props:
 *   model - parsed inversion model ({ iterations, cells, rmsPercent, name })
 *   name  - model display name
 *   onClose()
 */
export default function QCChartsModal({ model, name, onClose, projectId = null, storedId = null }) {
  const { C: themeC } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const [targetRms, setTargetRms] = useState(5);
  const [tab, setTab] = useState('qc');

  const iterations = model?.iterations || [];

  // Wide enough (EarthImager's main window): convergence and crossplot side by side in one view.
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.innerWidth * 0.94 >= WIDE_MODAL_PX);
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth * 0.94 >= WIDE_MODAL_PX);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const crossplotVisible = wide ? tab === 'qc' : tab === 'crossplot';

  // Iteration select: a session model carries every iteration's calculated values (dataFitIterations);
  // a stored model arrives without its data fit (GET /api/inversion/{id} strips it) and the datafit
  // endpoint serves each iteration (?iteration=n), cached here per iteration.
  const [pickedIter, setPickedIter] = useState(null); // null = the final iteration
  const [fits, setFits] = useState({}); // stored models: { [key]: fit }, key 'final' or the iteration number
  const requestedRef = useRef(new Set());
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  const { list: iterList, final: finalIter } = crossplotIterations(model);
  const selectedIter = pickedIter ?? finalIter;
  const fetchKey = pickedIter == null || pickedIter === finalIter ? 'final' : String(pickedIter);
  useEffect(() => {
    const id = `${storedId}:${fetchKey}`;
    if (!crossplotVisible || model?.dataFit || !storedId || requestedRef.current.has(id)) return;
    requestedRef.current.add(id);
    const done = (fit) => { if (mountedRef.current) setFits((f) => ({ ...f, [fetchKey]: fit })); };
    (fetchKey === 'final' ? inversionApi.datafit(storedId) : inversionApi.datafit(storedId, { iteration: Number(fetchKey) }))
      .then((res) => done(datafitToFit(res.data)))
      .catch(() => done({ rhoa_meas: [], rhoa_calc: [] }));
  }, [crossplotVisible, model?.dataFit, storedId, fetchKey]);
  const fetchedFit = fits[fetchKey] || null;
  const fitBusy = !model?.dataFit && !!storedId && crossplotVisible && !fetchedFit;
  const fit = model?.dataFit ? fitAtIteration(model.dataFit, model.dataFitIterations, selectedIter) || model.dataFit : fetchedFit;
  const options = iterationOptions(
    [...new Set([...iterList, ...(fit?.iteration != null ? [fit.iteration] : [])])].sort((a, b) => a - b), iterations);
  const iterationSelect = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
      <label style={{ fontSize: 10, color: C.text2 }}>
        Iteration{' '}
        <select data-testid="crossplot-iteration" value={fit?.iteration ?? selectedIter ?? ''} disabled={options.length < 2}
          onChange={(e) => setPickedIter(Number(e.target.value))}
          style={{ fontSize: 11, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, borderRadius: 6, padding: '2px 6px' }}>
          {options.length === 0 && <option value="">final</option>}
          {options.map((o) => <option key={o.n} value={o.n}>{o.label}</option>)}
        </select>
      </label>
      {options.length < 2 && (
        <span data-testid="crossplot-iteration-note" style={{ fontSize: 10, color: C.text3 }}>
          Only the final iteration's data fit is stored for this model.
        </span>
      )}
    </div>
  );
  const crossplotPane = (
    <div>
      {iterationSelect}
      {fitBusy ? <div style={{ fontSize: 12, color: C.text2 }}>Loading the stored data fit…</div> : <Crossplot C={C} fit={fit} />}
    </div>
  );

  // Misfit histogram from per-cell misfit values
  const misfitHist = useMemo(() => {
    const vals = (model?.cells || []).map(c => c.misfit).filter(v => typeof v === 'number' && isFinite(v));
    if (vals.length < 5) return null;
    const mean = vals.reduce((s, v) => s + v, 0) / vals.length;
    const std = Math.sqrt(vals.reduce((s, v) => s + (v - mean) ** 2, 0) / vals.length) || 1;
    const [min, max] = minMax(vals); // no spread: RangeError above ~120k cells (H5)
    const nBins = Math.min(24, Math.max(8, Math.round(Math.sqrt(vals.length))));
    const width = (max - min) / nBins || 1;
    const bins = Array.from({ length: nBins }, (_, i) => ({
      name: (min + (i + 0.5) * width).toPrecision(3),
      center: min + (i + 0.5) * width,
      count: 0,
    }));
    vals.forEach(v => {
      let idx = Math.floor((v - min) / width);
      if (idx >= nBins) idx = nBins - 1;
      bins[idx].count++;
    });
    return { bins, mean, std, n: vals.length };
  }, [model]);

  const converged = iterations.length >= 2 &&
    iterations[iterations.length - 1].rms < iterations[0].rms;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 130, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: wide ? 1180 : 760, maxWidth: '94vw', maxHeight: '90vh', background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'auto' }}>
        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, background: C.bg2, zIndex: 2 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.text0 }}>📊 Inversion QC — {name}</span>
          {model?.rmsPercent != null && (
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: model.rmsPercent <= targetRms ? 'rgba(52,211,153,0.12)' : 'rgba(251,191,36,0.12)', color: model.rmsPercent <= targetRms ? C.emerald : C.amber, border: `1px solid ${model.rmsPercent <= targetRms ? 'rgba(52,211,153,0.3)' : 'rgba(251,191,36,0.3)'}` }}>
              final RMS {model.rmsPercent}%
            </span>
          )}
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: 4, padding: '8px 16px 0' }} role="tablist">
          {(wide ? [['qc', 'Crossplot & convergence'], ['lcurve', 'L-curve']]
            : [['qc', 'Convergence & misfit'], ['crossplot', 'Crossplot'], ['lcurve', 'L-curve']]).map(([id, label]) => (
            <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`qc-tab-${id}`}
              style={{ fontSize: 11, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', background: tab === id ? C.bg4 : 'transparent', border: `1px solid ${tab === id ? C.border : 'transparent'}`, color: tab === id ? C.text0 : C.text3 }}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'crossplot' && !wide && <div style={{ padding: 16 }}>{crossplotPane}</div>}

        {tab === 'lcurve' && (
          <div style={{ padding: 16 }}><LCurveTab C={C} projectId={projectId} activeId={storedId} /></div>
        )}

        {tab === 'qc' && (
        <div data-testid={wide ? 'qc-side-by-side' : 'qc-single'} style={{ padding: 16, display: wide ? 'grid' : 'block', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* RMS Convergence */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>RMS Convergence</span>
              {iterations.length >= 2 && (
                <span style={{ fontSize: 10, color: converged ? C.emerald : C.red }}>
                  {converged ? '✓ converging' : '⚠ not decreasing — check inversion settings'}
                </span>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 10, color: C.text2 }}>Target RMS %</span>
              <input type="number" value={targetRms} min={0.5} max={50} step={0.5}
                onChange={e => setTargetRms(parseFloat(e.target.value) || 5)}
                style={{ width: 56, fontSize: 11, padding: '3px 6px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' }} />
            </div>
            {iterations.length === 0 ? (
              <div style={{ fontSize: 11, color: C.text3, padding: '18px 0', textAlign: 'center', background: C.bg3, borderRadius: 8 }}>
                No per-iteration RMS history found in this file.<br />
                Import the EarthImager .out/.inv log (with "Iteration N … RMS = x%" lines) to see convergence.
              </div>
            ) : (
              <div style={{ height: 190 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={iterations} margin={{ top: 6, right: 14, bottom: 4, left: -14 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="n" tick={{ fontSize: 9, fill: C.text3 }} stroke={C.border}
                      label={{ value: 'iteration', position: 'insideBottom', offset: -2, fontSize: 10, fill: C.text3 }} />
                    <YAxis tick={{ fontSize: 9, fill: C.text3 }} stroke={C.border} unit="%" />
                    <ChartTooltip contentStyle={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11 }}
                      itemStyle={{ color: C.text1 }} labelStyle={{ color: C.text0 }}
                      formatter={(v) => [`${v}%`, 'RMS']} labelFormatter={(v) => `Iteration ${v}`} />
                    <ReferenceLine y={targetRms} stroke={C.emerald} strokeDasharray="6 4"
                      label={{ value: `target ${targetRms}%`, fontSize: 9, fill: C.emerald, position: 'right' }} />
                    {selectedIter != null && iterations.some((it) => it.n === selectedIter) && (
                      <ReferenceLine x={selectedIter} stroke={C.amber} strokeWidth={2} data-testid="convergence-selected"
                        label={{ value: `it. ${selectedIter}`, fontSize: 9, fill: C.amber, position: 'top' }} />
                    )}
                    <Line dataKey="rms" stroke={C.cyan} strokeWidth={2} dot={{ r: 3, fill: C.cyan }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Misfit histogram */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>Data Misfit Distribution</span>
              {misfitHist && (
                <span style={{ fontSize: 10, color: C.text2, fontFamily: 'monospace' }}>
                  n={misfitHist.n} · μ={misfitHist.mean.toPrecision(3)} · σ={misfitHist.std.toPrecision(3)}
                </span>
              )}
            </div>
            {!misfitHist ? (
              <div style={{ fontSize: 11, color: C.text3, padding: '18px 0', textAlign: 'center', background: C.bg3, borderRadius: 8 }}>
                No per-cell misfit column in this model.<br />
                Export the inversion with misfit/error per cell to see the distribution (should be Gaussian around 0).
              </div>
            ) : (
              <div style={{ height: 190 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={misfitHist.bins} margin={{ top: 6, right: 14, bottom: 4, left: -14 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 8, fill: C.text3 }} stroke={C.border} interval={2} />
                    <YAxis tick={{ fontSize: 9, fill: C.text3 }} stroke={C.border} allowDecimals={false} />
                    <ChartTooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                      contentStyle={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11 }}
                      itemStyle={{ color: C.text1 }} labelStyle={{ color: C.text0 }} />
                    <ReferenceLine x={misfitHist.bins.reduce((best, b) => Math.abs(b.center - misfitHist.mean) < Math.abs(best.center - misfitHist.mean) ? b : best, misfitHist.bins[0]).name}
                      stroke={C.text2} strokeDasharray="4 3" />
                    <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                      {misfitHist.bins.map((b, i) => (
                        <Cell key={i} fill={Math.abs(b.center - misfitHist.mean) > 2 * misfitHist.std ? C.red : C.cyan} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {misfitHist && (
              <div style={{ fontSize: 9, color: C.text3, marginTop: 4 }}>
                Red bars = outliers beyond 2σ — inspect those measurements. A good inversion is Gaussian centered near 0.
              </div>
            )}
          </div>

          <div style={{ fontSize: 9, color: C.text3, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
            Electrode contact resistance is a property of the raw survey file; see the Survey card on the dataset page.
          </div>
        </div>
        {wide && crossplotPane}
        </div>
        )}
      </div>
    </div>
  );
}
